import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ProductService } from '../../../shared/services/catalog.service';
import {
    HcModal,
    HcSort,
    HcToastOptions,
    HcToasterService,
    PaginationComponent
} from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from '../../../shared/services/change.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ComponentCanDeactivate } from '../../can-deactivate/component-can-deactivate';
import { OktaService } from '../../../shared/services/okta.service';
import { StatusElement, StatusService } from '../../../shared/services/status.service';
import { ItemService } from '../../../shared/services/item.service';
import { SourceElement, SourceService } from '../../../shared/services/source.service';
import { TypeElement, TypeService } from '../../../shared/services/type.service';

/** Admin source element editor */
@Component({
    selector: 'pd-admin-sources-editor',
    templateUrl: './admin-sources-editor.component.html',
    styleUrls: ['./admin-sources-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminSourcesEditorComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    modalRef: HcModal<TemplateRef<any>>;
    selectedTab = 0;
    source: SourceElement;
    sourceLoading = true;
    sourceError = false;
    sourceID: number;
    itemID: string;
    savedItem = '';
    revisionDate = '';
    sourceForm = new FormGroup({
        nameControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        IDControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        statusControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        typeControl: new FormControl<number | null>(null, { validators: [Validators.required] })
    });

    statuses: StatusElement[] = [];
    types: TypeElement[] = [];

    @ViewChild(HcSort)
    sort: HcSort;
    
    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get loading(): boolean {
        return this.sourceLoading;
    }

    get loadError(): boolean {
        return this.sourceError;
    }

    get sourceInputsDirty(): boolean {
        return this.sourceForm.dirty;
    }

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private changeService: ChangeService,
        private toasterService: HcToasterService,
        private oktaService: OktaService,
        private statusService: StatusService,
        private itemService: ItemService,
        private sourceService: SourceService,
        private typeService: TypeService
    ) {
        super();
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a source ID is supplied, we're editing an existing source
            if (params['id']) {
                this.sourceID = params['id'];
            } else {
                // Otherwise we're creating a new source so generate a new item ID for the source
                this.itemService.getNewID().pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {                            
                        this.itemID = data['itemNumber'];
                        this.sourceForm.controls['IDControl'].setValue(data['itemNumber'].toString());
                        this.sourceError = false;
                        this.sourceLoading = false;
                    },
                    error: msg => { 
                        console.log( msg );
                        this.sourceError = true;
                        this.sourceLoading = false;
                    }
                });
            }
        });

        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statuses = this.sortAlphabetically(list);
                if (this.types.length > 0) {
                    this.sourceID && this.loadSource();
                }
            } else {
                !this.statusService.loadFailed ? this.statusService.refresh() : this.sourceError = true;
            }
        });

        this.typeService.catTypesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.types = this.sortAlphabetically(list);
                if (this.statuses.length > 0) {
                    this.sourceID && this.loadSource();
                }
            } else {
                !this.typeService.loadFailed ? this.typeService.loadCatTypes('source') : this.sourceError = true;
            }
        });
    }

    //alphabetical sort helper function
    sortAlphabetically(list: any[]) {
        return list.sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }

    //check if the Item ID is already in use and show an error on the form field if it is
    checkID( doSave: boolean ): void {
        const currentID = this.sourceForm.controls['IDControl'].value;

        if ( this.savedItem !== currentID ) {
            if ( Number.isNaN(Number(currentID)) ) {
                this.itemService.checkSource(currentID).pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {        
                        if ( data['itemExists'] ) {
                            this.sourceForm.controls['IDControl'].setErrors({ valid: false });
                        } else if ( doSave ) {
                            this.onClickSave();
                        }
                    },
                    error: msg => { 
                        console.log( msg );
                    }
                });
            } else {
                this.itemService.checkID(Number(currentID)).pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {        
                        if ( data['itemExists'] ) {
                            this.sourceForm.controls['IDControl'].setErrors({ valid: false });
                        } else if ( doSave ) {
                            this.onClickSave();
                        }
                    },
                    error: msg => { 
                        console.log( msg );
                    }
                });
            }
        } else if ( doSave ) {
            this.onClickSave();
        }
    }

    loadSource(): void {
        this.sourceService
            .detail(this.sourceID)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    this.revisionDate = data[0].revisionDate;
                    this.itemID = data[0].item;
                    this.savedItem = data[0].item;

                    this.sourceError = false;
                    this.sourceLoading = false;

                    //patch fields with source data
                    this.sourceForm.patchValue({
                        nameControl: data[0].name,
                        IDControl: data[0].item,
                        statusControl: data[0].statusID,
                        typeControl: data[0].typeID
                    });

                    this.source = {
                        sourceID: data[0].sourceID,
                        name: data[0].name,
                        item: data[0].item,
                        statusID: data[0].statusID,
                        typeID: data[0].typeID,
                        revisionDate: data[0].revisionDate
                    };

                    this.sourceForm.markAsPristine();
                },
                error: msg => {
                    this.sourceError = true;
                    this.sourceLoading = false;
                    console.log(msg);
                }
            });
    }

    onClickSave() {
        this.saveSource();
    }
    
    //// Check for changes in all source attributes and log them ////
    logChanges(source: SourceElement) {
        let changes: ChangeElement[] = [];
        const date = new Date().toISOString();

        //check if name changed
        if (this.source.name !== source.name) {
            const change = {
                item: Number.isNaN(Number(source.item)) ? 0 : Number(source.item),
                name: source.name,
                category: 'source',
                field: 'Name',
                previous: this.source.name,
                update: source.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if item changed
        if (this.source.item !== source.item) {
            const change = {
                item: Number.isNaN(Number(source.item)) ? 0 : Number(source.item),
                name: source.name,
                category: 'source',
                field: 'Item ID',
                previous: this.source.item.toString(),
                update: source.item.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if type changed
        if (this.source.typeID !== source.typeID) {
            const change = {
                item: Number.isNaN(Number(source.item)) ? 0 : Number(source.item),
                name: source.name,
                category: 'source',
                field: 'Type',
                previous: this.source.typeID.toString(),
                update: source.typeID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if status changed
        if (this.source.statusID !== source.statusID) {
            const change = {
                item: Number.isNaN(Number(source.item)) ? 0 : Number(source.item),
                name: source.name,
                category: 'source',
                field: 'Status',
                previous: this.source.statusID.toString(),
                update: source.statusID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        if (changes.length > 0) {
            this.changeService.add(changes);
        }
    }

    saveSource() {
        //grab update date
        const date = new Date().toISOString();

        //create the source object to save
        const source: SourceElement = {
            sourceID: this.sourceID || -1,
            name: this.sourceForm.controls['nameControl'].value,
            item: this.sourceForm.controls['IDControl'].value,
            statusID: this.sourceForm.controls['statusControl'].value!,
            typeID: this.sourceForm.controls['typeControl'].value!,
            revisionDate: date
        };

        //if the source is being edited, update it, log changes, and load changes
        if (this.sourceID) {
            this.sourceService
                .update(source)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: () => {
                        this.logChanges(source);
                        this.sourceService.refresh();
                        this.productService.refresh();

                        const options: HcToastOptions = {
                            header: 'Source Updated',
                            body: source.name + ' successfully updated.',
                            type: 'success',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);

                        //mark everything pristine, update source variable, and update revision date
                        this.sourceForm.markAsPristine();
                        this.source = source;
                        this.revisionDate = date;
                    },
                    error: msg => {
                        const options: HcToastOptions = {
                            header: 'Update Failed',
                            body: 'Unable to update source. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        } else {
            //if the source is being added, add it, log an add change, update the item ID, and load changes
            this.sourceService
                .add(source)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: result => {
                        this.changeService.add([
                            {
                                item: Number.isNaN(Number(source.item)) ? 0 : Number(source.item),
                                name: source.name,
                                category: 'source',
                                field: '--',
                                previous: '--',
                                update: 'Source added',
                                user: this.oktaService.username.value,
                                date: new Date().toISOString()
                            }
                        ]);
                        this.sourceService.refresh();
                        this.sourceID = result;
                        this.itemID = source.item;
                        this.savedItem = source.item;

                        const options: HcToastOptions = {
                            header: 'Source Added',
                            body: source.name + ' successfully added.',
                            type: 'success',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);

                        //mark everything pristine and update source variable
                        this.sourceForm.markAsPristine();
                        this.source = source;
                        this.revisionDate = date;
                    },
                    error: msg => {
                        const options: HcToastOptions = {
                            header: 'Add Failed',
                            body: 'Unable to add source. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        }
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    canDeactivate(): boolean {
        return !this.sourceInputsDirty;
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
